<template>
  <Dialog
    v-model="dialogVisible"
    title="Edit Medication"
    dialog-type="primary"
    dialog-size="medium"
    :close-on-overlay="false"
    :z-index="3000"
    @close="closeDialog"
  >
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 18 18" fill="none">
        <path d="M5.625 18C4.05833 18 2.72917 17.4542 1.6375 16.3625C0.545833 15.2708 0 13.9417 0 12.375C0 11.625 0.141667 10.9083 0.425 10.225C0.708333 9.54167 1.11667 8.93333 1.65 8.4L8.4 1.65C8.93333 1.11667 9.54167 0.708333 10.225 0.425C10.9083 0.141667 11.625 0 12.375 0C13.9417 0 15.2708 0.545833 16.3625 1.6375C17.4542 2.72917 18 4.05833 18 5.625C18 6.375 17.8583 7.09167 17.575 7.775C17.2917 8.45833 16.8833 9.06667 16.35 9.6L9.6 16.35C9.06667 16.8833 8.45833 17.2917 7.775 17.575C7.09167 17.8583 6.375 18 5.625 18ZM12.275 10.85L14.95 8.2C15.2833 7.86667 15.5417 7.475 15.725 7.025C15.9083 6.575 16 6.10833 16 5.625C16 4.625 15.6458 3.77083 14.9375 3.0625C14.2292 2.35417 13.375 2 12.375 2C11.8917 2 11.425 2.09167 10.975 2.275C10.525 2.45833 10.1333 2.71667 9.8 3.05L7.15 5.725L12.275 10.85ZM5.625 16C6.10833 16 6.575 15.9083 7.025 15.725C7.475 15.5417 7.86667 15.2833 8.2 14.95L10.85 12.275L5.725 7.15L3.05 9.8C2.71667 10.1333 2.45833 10.525 2.275 10.975C2.09167 11.425 2 11.8917 2 12.375C2 13.375 2.35417 14.2292 3.0625 14.9375C3.77083 15.6458 4.625 16 5.625 16Z" fill="white"/>
      </svg>
    </template>

    <!-- Medication Count Row -->
    <div class="medication-count-row">
      <span class="medication-count-text">{{ filteredTotal }} existing medications</span>
    </div>

    <!-- Search and Filter Row -->
    <div class="search-filter-row">
      <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M14.2939 12.5786H13.3905L13.0703 12.2699C14.191 10.9663 14.8656 9.27387 14.8656 7.43282C14.8656 3.32762 11.538 0 7.43282 0C3.32762 0 0 3.32762 0 7.43282C0 11.538 3.32762 14.8656 7.43282 14.8656C9.27387 14.8656 10.9663 14.191 12.2699 13.0703L12.5786 13.3905V14.2939L18.2962 20L20 18.2962L14.2939 12.5786ZM7.43282 12.5786C4.58548 12.5786 2.28702 10.2802 2.28702 7.43282C2.28702 4.58548 4.58548 2.28702 7.43282 2.28702C10.2802 2.28702 12.5786 4.58548 12.5786 7.43282C12.5786 10.2802 10.2802 12.5786 7.43282 12.5786Z" fill="#374957"/>
      </svg>
      <el-autocomplete
        v-model="searchQuery"
        :fetch-suggestions="queryMedications"
        placeholder="Search medication"
        class="medication-form-select"
        popper-class="medication-autocomplete-popper"
        :trigger-on-focus="true"
        :highlight-first-item="true"
        :clearable="true"
        @select="handleMedicationSelect"
        @clear="handleMedicationClear"
      />
      <svg class="filter-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M10 18H14V16H10V18ZM3 6V8H21V6H3ZM6 13H18V11H6V13Z" fill="#4A4A4A"/>
      </svg>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="-"
        start-placeholder="Start date"
        end-placeholder="End date"
        class="date-range-picker"
        popper-class="medication-date-picker-popper"
        format="MM/DD/YYYY"
        value-format="YYYY-MM-DD"
      />
      <ResponsiveButton
        display_name="Add New"
        button_type="filled"
        built_in_theme="dark"
        size="medium"
        button_border_radius="20px"
        customized_class="add-new-foldable-btn"
        @click-button="handleAddNew"
      />
    </div>

    <!-- Dashed Line -->
    <div class="dashed-line"></div>

    <!-- Medications List -->
    <div class="medications-list">
      <div v-if="isLoading" class="loading-state">
        <p>Loading medications...</p>
      </div>
      <div v-else-if="paginatedMedications.length === 0" class="empty-state">
        <p>No medications found</p>
      </div>
      <div
        v-for="med in paginatedMedications"
        v-show="!isLoading"
        :key="med.id"
        class="medication-list-item"
      >
        <div class="medication-info">
          <p class="medication-name">{{ med.name }}</p>
          <p v-if="hasMedicationDetails(med)" class="medication-details">
            <template v-if="med.dose">{{ med.dose }}</template>
            <template v-if="med.dose && (med.frequencyNumber && med.frequencyPeriod)">, </template>
            <template v-if="med.frequencyNumber && med.frequencyPeriod">{{ med.frequencyNumber }} {{ med.frequencyPeriod }}</template>
            <template v-if="(med.dose || (med.frequencyNumber && med.frequencyPeriod)) && med.startDate && formatDate(med.startDate) !== 'N/A'"> | Started on: {{ formatDate(med.startDate) }}</template>
            <template v-if="!(med.dose || (med.frequencyNumber && med.frequencyPeriod)) && med.startDate && formatDate(med.startDate) !== 'N/A'">Started on: {{ formatDate(med.startDate) }}</template>
          </p>
        </div>
        <div class="medication-actions">
          <button class="edit-btn" @click="handleEdit(med)">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M21.4555 5.41575C21.6477 5.70687 21.6157 6.10248 21.3594 6.35876L12.167 15.5511C12.0728 15.6454 11.9552 15.7128 11.8263 15.7465L7.99783 16.7465C7.87296 16.7791 7.74425 16.7784 7.62332 16.7476C7.49475 16.7149 7.37498 16.6482 7.27797 16.5511C7.08969 16.3629 7.01535 16.0889 7.08264 15.8313L8.08264 12.0028C8.11211 11.89 8.1674 11.7786 8.24389 11.6912L17.4703 2.46967C17.5511 2.38891 17.6484 2.32846 17.7543 2.29163C17.8328 2.26432 17.916 2.25 18.0007 2.25C18.1996 2.25 18.3903 2.32902 18.531 2.46967L21.3594 5.2981C21.396 5.33471 21.4281 5.37416 21.4555 5.41575ZM19.7684 5.82843L18.0007 4.06066L9.48251 12.5788L8.85752 14.9716L11.2503 14.3466L19.7684 5.82843Z" fill="#374957"/>
              <path d="M19.642 17.1603C19.9154 14.8227 20.0024 12.4688 19.903 10.1208C19.8982 10.0084 19.9405 9.89898 20.0201 9.81942L21.0034 8.83609C21.1243 8.71519 21.3309 8.79194 21.3422 8.96254C21.5271 11.7522 21.457 14.5545 21.1319 17.3346C20.8953 19.3571 19.271 20.9421 17.2589 21.167C13.7924 21.5544 10.209 21.5544 6.74244 21.167C4.73038 20.9421 3.10605 19.3571 2.8695 17.3346C2.45496 13.7903 2.45496 10.2097 2.8695 6.66543C3.10605 4.6429 4.73038 3.05789 6.74244 2.83301C9.37219 2.5391 12.0691 2.46815 14.7313 2.62016C14.9029 2.62996 14.9811 2.83757 14.8596 2.95909L13.867 3.95165C13.7883 4.03034 13.6804 4.07261 13.5692 4.06885C11.3428 3.99376 9.10122 4.07872 6.90905 4.32373C5.57894 4.47239 4.51346 5.522 4.35934 6.83968C3.95834 10.2682 3.95834 13.7318 4.35934 17.1603C4.51346 18.478 5.57894 19.5276 6.90905 19.6763C10.2649 20.0513 13.7365 20.0513 17.0923 19.6763C18.4224 19.5276 19.4879 18.478 19.642 17.1603Z" fill="#374957"/>
            </svg>
          </button>
          <button class="delete-btn" @click="handleDelete(med)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M11 0V1H16V3H15V16C15 17.1 14.1 18 13 18H3C1.9 18 1 17.1 1 16V3H0V1H5V0H11ZM3 16H13V3H3V16ZM5 5H7V14H5V5ZM11 5H9V14H11V5Z" fill="#D10000"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-wrapper">
      <Pagination
        :total_items="filteredTotal"
        :current_page="currentPage"
        :per_page="pageSize"
        :per_page_options="[5, 10, 15]"
        item_label="medications"
        @update:current_page="$emit('page-change', $event)"
        @update:per_page="$emit('page-size-change', $event)"
      />
    </div>

  </Dialog>
</template>

<script>
import { deleteMedicationRecord, searchDrugsByPrefix } from '@/api/medicationApi';
import { notify } from '@/utils/notify';

export default {
  name: 'MedicationsListDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    medications: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    pageSize: {
      type: Number,
      default: 5
    },
    currentPage: {
      type: Number,
      default: 1
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    patientId: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'add-new', 'edit', 'page-change', 'page-size-change', 'deleted'],
  data() {
    return {
      searchQuery: '',
      dateRange: null,
      localMedications: [],
      commonMedications: [
        'Aspirin',
        'Lipitor (Atorvastatin)',
        'Metformin',
        'Lisinopril',
        'Amlodipine',
        'Metoprolol',
        'Omeprazole',
        'Simvastatin',
        'Losartan',
        'Gabapentin',
        'Hydrochlorothiazide',
        'Furosemide',
        'Pantoprazole',
        'Prednisone',
        'Ibuprofen',
        'Levothyroxine',
        'Amoxicillin',
        'Azithromycin',
        'Ciprofloxacin',
        'Cephalexin'
      ]
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    displayMedications() {
      let filtered = [...this.medications];

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(med =>
          med.name?.toLowerCase().includes(query) ||
          med.dose?.toLowerCase().includes(query)
        );
      }

      // Filter by date range
      if (this.dateRange && this.dateRange.length === 2) {
        const [startDate, endDate] = this.dateRange;
        filtered = filtered.filter(med => {
          if (!med.startDate) return false;
          // Compare as strings to avoid timezone issues
          return med.startDate >= startDate && med.startDate <= endDate;
        });
      }

      return filtered;
    },
    // Client-side pagination
    paginatedMedications() {
      const filtered = this.displayMedications;
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return filtered.slice(start, end);
    },
    // Total count after filtering
    filteredTotal() {
      return this.displayMedications.length;
    }
  },
  watch: {
    medications: {
      immediate: true,
      handler(newVal) {
        this.localMedications = [...(newVal || [])];
      }
    },
    visible(newVal) {
      if (newVal) {
        this.localMedications = [...(this.medications || [])];
        this.searchQuery = '';
        this.dateRange = null;
        // Reset to page 1 and emit to parent
        this.$emit('page-change', 1);
      }
    }
  },
  methods: {
    async queryMedications(queryString, cb) {
      // Only search if we have at least 1 character input
      if (!queryString || queryString.length < 1) {
        cb([]);
        return;
      }

      try {
        const drugNames = await searchDrugsByPrefix(queryString);
        // Use fallback data if API returns empty array (staging/development)
        const results = drugNames.length > 0
          ? drugNames.map(name => ({ value: name }))
          : this.commonMedications
              .filter(med => med.toLowerCase().includes(queryString.toLowerCase()))
              .map(med => ({ value: med }));
        cb(results);
      } catch (error) {
        console.error('Error searching medications:', error);
        // Fallback to common medications on error
        const fallbackResults = this.commonMedications
          .filter(med => med.toLowerCase().includes(queryString.toLowerCase()))
          .map(med => ({ value: med }));
        cb(fallbackResults);
      }
    },
    handleMedicationSelect(item) {
      this.searchQuery = item.value;
    },
    handleMedicationClear() {
      this.searchQuery = '';
    },
    closeDialog() {
      this.$emit('close');
    },
    handleAddNew() {
      this.$emit('add-new');
    },
    handleEdit(medication) {
      this.$emit('edit', medication);
    },
    async handleDelete(medication) {
      if (!this.patientId) {
        notify({
          title: 'Error',
          message: 'Patient ID is required',
          type: 'error',
          duration: 3000
        });
        return;
      }

      try {
        await deleteMedicationRecord(this.patientId, medication.id);

        // Success notification
        notify({
          title: 'Success',
          message: 'Medication Deleted Successfully',
          type: 'success',
          duration: 3000
        });

        // Remove from local list
        const index = this.localMedications.findIndex(m => m.id === medication.id);
        if (index > -1) {
          this.localMedications.splice(index, 1);
        }

        // Emit deleted event to parent to refresh from API
        this.$emit('deleted', medication.id);
      } catch (error) {
        console.error('Failed to delete medication:', error);
        notify({
          title: 'Error',
          message: 'Failed to Delete Medication',
          type: 'error',
          duration: 3000
        });
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      try {
        // Parse YYYY-MM-DD manually to avoid UTC conversion
        const [year, month, day] = dateStr.split('-').map(Number);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[month - 1]} ${String(day).padStart(2, '0')}, ${year}`;
      } catch (e) {
        return dateStr;
      }
    },
    hasMedicationDetails(med) {
      const hasDose = !!med.dose;
      const hasFrequency = !!(med.frequencyNumber && med.frequencyPeriod);
      const hasStartDate = !!(med.startDate && this.formatDate(med.startDate) !== 'N/A');
      return hasDose || hasFrequency || hasStartDate;
    }
  }
};
</script>

<style>
/* Autocomplete dropdown is teleported to <body>, so it can't live in scoped styles.
   The .medication-autocomplete-popper class is unique to this component. */
.medication-autocomplete-popper .el-autocomplete__popper {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
}

.medication-autocomplete-popper .el-autocomplete-suggestion li {
  font-size: 14px;
  padding: 0 12px;
  line-height: 40px;
}

.medication-autocomplete-popper .el-autocomplete-suggestion li.highlighted {
  background-color: #F5F7FA;
}

.medication-date-picker-popper.el-popper {
  z-index: 3100 !important;
}
</style>

<style scoped>
/* Input styles - anchored on unique component classes via :deep() so they don't leak. */
:deep(.medication-form-select .el-input__wrapper) {
  border: 1px solid #c4c4c4 !important;
  box-shadow: none !important;
  height: 40px !important;
  border-radius: 4px !important;
  background-color: #fff !important;
}

:deep(.medication-form-select .el-input__wrapper:hover),
:deep(.medication-form-select .el-input__wrapper.is-focus) {
  border: 1px solid #c4c4c4 !important;
  box-shadow: none !important;
}

:deep(.date-range-picker.el-date-editor.el-range-editor.el-input__wrapper) {
  border: 1px solid #c4c4c4 !important;
  box-shadow: none !important;
  height: 40px !important;
  border-radius: 4px !important;
  background-color: #fff !important;
}

:deep(.date-range-picker.el-date-editor.el-range-editor.el-input__wrapper:hover),
:deep(.date-range-picker.el-date-editor.el-range-editor.el-input__wrapper.is-focus),
:deep(.date-range-picker.el-date-editor.el-range-editor.el-input__wrapper.is-active) {
  border: 1px solid #c4c4c4 !important;
  box-shadow: none !important;
}

:deep(.date-range-picker .el-range-input) {
  height: 38px !important;
  line-height: 38px !important;
  font-size: 14px;
}

:deep(.medication-form-select .el-input__inner::placeholder),
:deep(.date-range-picker .el-input__inner::placeholder) {
  font-size: 14px !important;
  color: #999 !important;
}

:deep(.medication-form-select .el-input__suffix .el-icon:not(.el-icon-circle-close)) {
  display: none !important;
}

.medication-count-row {
  padding: 24px 24px 0;
  margin-bottom: 12px;
}

.search-filter-row {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 0 24px 16px;
}

.search-icon {
  flex-shrink: 0;
}

.filter-icon {
  flex-shrink: 0;
}

.medication-form-select {
  width: 200px;
}

.medication-count-text {
  color: #333;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.4px;
}

/* Date picker width */
:deep(.date-range-picker) {
  width: 280px !important;
  flex-shrink: 0;
}

.dashed-line {
  border-top: 1px dashed #C4C4C4;
  margin: 0 24px 16px;
}

.pagination-wrapper {
  padding: 12px 24px 16px;
}

/* Keep "Add New" on one line — library uses scoped data attrs so we set this
   non-scoped via :deep on the customized_class. */
:deep(.add-new-foldable-btn) {
  flex-shrink: 0;
  white-space: nowrap;
}

.medications-list {
  min-height: 350px;
  max-height: 450px;
  overflow-y: auto;
  padding: 0 24px;
}

.medications-list::-webkit-scrollbar {
  width: 6px;
}

.medications-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.medications-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.medications-list::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.medication-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #E5E5E5;
  transition: background-color 0.2s;
}

.medication-list-item:last-child {
  border-bottom: none;
}

.medication-info {
  flex: 1;
}

.medication-name {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px 0;
  line-height: 20px;
}

.medication-date {
  font-family: Inter;
  font-size: 12px;
  color: #666;
  margin: 0;
}

.medication-details {
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: var(--SecondaryBlack, #666);
  margin: 0;
}

.medication-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.edit-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.edit-btn:hover {
  background-color: rgba(55, 73, 87, 0.1);
}

.delete-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.delete-btn:hover {
  background-color: #FEEBEB;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-style: italic;
}

.loading-state {
  padding: 40px 20px;
  text-align: center;
  color: #666;
  font-style: italic;
}
</style>
